import { useState, type FormEvent, type ReactNode } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/utils/cn';

/**
 * A dialog wrapping a form.
 *
 * Encodes the portal pattern once so no form has to remember it: the shell does not
 * clip, the body scrolls through `DialogBody`, and pickers inside get a dialog to
 * portal into. Every field inside should carry `popoverPortal="dialog"`.
 *
 * Prefer `basic` + `more` for long records — essential fields stay on the first tab,
 * everything else on the second — so a clerk is not forced through a tall wall of
 * optional detail to add a visitor.
 *
 * Reference: documentation/architecture/dialog-dropdown-portal-architecture.md.
 */

export interface FormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  /** Simple body. Ignored when `basic` is set. */
  children?: ReactNode;
  /**
   * Essential fields shown first. When set with `more`, the dialog uses a
   * basic / more-details split instead of a single scrolling stack.
   */
  basic?: ReactNode;
  /** Secondary fields — membership history, addresses, notes, etc. */
  more?: ReactNode;
  basicLabel?: string;
  moreLabel?: string;
  onSubmit: () => void | Promise<void>;
  submitLabel: string;
  cancelLabel: string;
  closeLabel?: string;
  submitting?: boolean;
  submitDisabled?: boolean;
  /** Hide the footer actions. The corner close control remains. */
  hideFooter?: boolean;
  destructive?: boolean;
  /** Wide by default for forms; use `md`/`sm` for short prompts. */
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  /** Extra footer content on the left — a secondary action, a note. */
  footerLeading?: ReactNode;
  /** Override the body scroll height. */
  bodyClassName?: string;
}

export const FormDialog = ({
  open,
  onOpenChange,
  title,
  description,
  children,
  basic,
  more,
  basicLabel = 'Basic',
  moreLabel = 'More details',
  onSubmit,
  submitLabel,
  cancelLabel,
  closeLabel,
  submitting = false,
  submitDisabled = false,
  hideFooter = false,
  destructive = false,
  size = 'xl',
  footerLeading,
  bodyClassName,
}: FormDialogProps) => {
  const [section, setSection] = useState('basic');
  const split = basic !== undefined && more !== undefined;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (submitting || submitDisabled) return;
    void onSubmit();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) setSection('basic');
        onOpenChange(next);
      }}
    >
      <DialogContent size={size} closeLabel={closeLabel}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description ? <DialogDescription>{description}</DialogDescription> : null}
        </DialogHeader>

        <form onSubmit={submit} className="flex min-h-0 flex-col gap-4">
          {split ? (
            <Tabs value={section} onValueChange={setSection} className="min-h-0">
              <TabsList className="shrink-0">
                <TabsTrigger value="basic">{basicLabel}</TabsTrigger>
                <TabsTrigger value="more">{moreLabel}</TabsTrigger>
              </TabsList>
              <DialogBody className={cn(bodyClassName)}>
                <TabsContent value="basic" className="mt-0 pt-0">
                  <div className="space-y-4">{basic}</div>
                </TabsContent>
                <TabsContent value="more" className="mt-0 pt-0">
                  <div className="space-y-4">{more}</div>
                </TabsContent>
              </DialogBody>
            </Tabs>
          ) : (
            <DialogBody className={cn(bodyClassName)}>
              <div className="space-y-4">{basic ?? children}</div>
            </DialogBody>
          )}

          {hideFooter ? null : (
            <DialogFooter>
              {footerLeading ? (
                <div className="flex flex-1 items-center sm:justify-start">{footerLeading}</div>
              ) : null}
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={submitting}
              >
                {cancelLabel}
              </Button>
              <Button
                type="submit"
                variant={destructive ? 'destructive' : 'primary'}
                loading={submitting}
                disabled={submitDisabled}
              >
                {submitLabel}
              </Button>
            </DialogFooter>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
};
